import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";
import { useResendVerification } from "../hooks/useResendVerification";
import { useResendCooldown } from "../hooks/useResendCooldown";
import type { ApiError } from "../api/http";
import { loginErrorKey } from "../utils/authErrors";
import { setPendingVerificationEmail } from "../utils/pendingVerification";
import { AuthLayout } from "../components/auth/AuthLayout";
import { PasswordInput } from "../components/auth/PasswordInput";
import {
  authFormError,
  authInput,
  authLabel,
  authLink,
  authMuted,
  authPrimaryBtn,
} from "../components/auth/authStyles";

export function LoginPage() {
  const { login } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const resend = useResendVerification();
  const { cooldown, start: startCooldown } = useResendCooldown("login");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setNeedsVerification(false);
    setIsSubmitting(true);

    try {
      await login({ email, password });
      addToast(t("auth.login.welcomeToast"), "success");
      // Return to the page the user was originally heading for (set by
      // ProtectedRoute); same-origin path guard against open redirects.
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from.startsWith("/") ? from : "/");
    } catch (err) {
      const apiError = err as ApiError;
      setError(t(loginErrorKey(apiError)));
      if (apiError.status === 403) {
        // Unverified account — offer an inline resend instead of a dead end.
        setNeedsVerification(true);
        setPendingVerificationEmail(email);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resend.isPending || !email) return;
    try {
      await resend.mutateAsync({ email });
    } catch {
      // Enumeration-safe endpoint — surface the same affordance either way.
    }
    startCooldown();
    addToast(t("auth.login.verificationSent"), "success");
  };

  return (
    <AuthLayout title={t("auth.login.subtitle")}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className={authFormError}>
            {error}
            {needsVerification && (
              <button
                type="button"
                onClick={handleResend}
                disabled={cooldown > 0 || resend.isPending}
                className={`mt-2 block text-left ${authLink} disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {resend.isPending
                  ? t("auth.verify.resending")
                  : cooldown > 0
                    ? t("auth.verify.resendCooldown", { seconds: cooldown })
                    : t("auth.login.resendVerification")}
              </button>
            )}
          </div>
        )}

        <div>
          <label htmlFor="email" className={authLabel}>
            {t("auth.login.email")}
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={authInput}
            placeholder={t("auth.login.emailPlaceholder")}
          />
        </div>

        <div>
          <label htmlFor="password" className={authLabel}>
            {t("auth.login.password")}
          </label>
          <PasswordInput
            id="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("auth.login.passwordPlaceholder")}
          />
        </div>

        <button type="submit" disabled={isSubmitting} className={authPrimaryBtn}>
          {isSubmitting ? t("auth.login.submitting") : t("auth.login.submit")}
        </button>
      </form>

      <div className={`mt-4 text-center text-sm ${authMuted}`}>
        <Link to="/forgot-password" className={authLink}>
          {t("auth.login.forgotLink")}
        </Link>
      </div>

      <div className={`mt-5 border-t border-slate-200 pt-4 text-center text-sm dark:border-white/5 ${authMuted}`}>
        {t("auth.login.signupPrompt")}{" "}
        <Link to="/register" className={authLink}>
          {t("auth.login.signupLink")}
        </Link>
      </div>
    </AuthLayout>
  );
}
